/**
 * AISettingsPanel Component
 * Apple-quality design for AI nurture settings with full email preview
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  XMarkIcon,
  PlusIcon,
  ChevronDownIcon,
  PencilIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const SIGN_OFFS = [
  { id: 'warmly', label: 'Warmly', preview: 'Warmly,' },
  { id: 'best', label: 'Best', preview: 'Best,' },
  { id: 'cheers', label: 'Cheers', preview: 'Cheers,' },
  { id: 'thanks', label: 'Thanks', preview: 'Thanks,' },
  { id: 'talk_soon', label: 'Talk soon', preview: 'Talk soon,' },
  { id: 'looking_forward', label: 'Looking forward', preview: 'Looking forward to connecting,' }
];

const TONES = [
  {
    id: 'friendly',
    name: 'Friendly',
    description: 'Warm and conversational',
    subject: 'So excited to hear from you!',
    bodyTemplate: `Hi {{name}}!

Thank you so much for reaching out - I'm genuinely thrilled you're considering me for your {{project_type}}!

I took a look at your inquiry and I can already tell this is going to be something special. {{event_date}} is a beautiful time, and I'd love to be part of capturing those memories for you.

I have a few questions that would help me understand your vision better. Would you be up for a quick call this week? I promise to keep it short and sweet - just want to make sure I can deliver exactly what you're dreaming of.

Looking forward to connecting!`
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Polished and businesslike',
    subject: 'Thank you for your inquiry',
    bodyTemplate: `Dear {{name}},

Thank you for your inquiry regarding {{project_type}} services. I received your message and wanted to reach out personally to discuss your requirements.

Based on the details you've provided, I believe we could create something truly memorable together. I would welcome the opportunity to learn more about your vision and how I can best serve your needs.

Would you be available for a brief consultation call? I'm happy to work around your schedule to find a convenient time.`
  },
  {
    id: 'casual',
    name: 'Casual',
    description: 'Relaxed and easy-going',
    subject: 'Hey! Got your message',
    bodyTemplate: `Hey {{name}}!

Got your message about your {{project_type}} - sounds awesome! I'm definitely interested in learning more.

I checked out a few details and I think we could create something really cool together. Let's hop on a quick call and chat about your ideas?

Just let me know what works for you!`
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Define your own voice',
    subject: '',
    bodyTemplate: ''
  }
];

const DEFAULT_CADENCE = {
  cold: [0.25, 24, 72, 168, 336],
  warm: [0.25, 12, 48, 120],
  hot: [0]
};

const AISettingsPanel = ({ settings, onChange }) => {
  const [showCadence, setShowCadence] = useState(false);
  const [newTopic, setNewTopic] = useState({ emphasize: '', avoid: '' });
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  const signature = settings?.signature || {};
  const hasValidSignature = signature.name && signature.email;

  const aiSettings = settings?.aiSettings || {
    enabled: true,
    tone: 'friendly',
    signOff: 'warmly',
    customToneDescription: '',
    customSubject: '',
    customBody: '',
    avoidTopics: [],
    emphasizeTopics: [],
    maxEmailsPerLead: 5,
    stopOnReply: true,
    includePortfolio: true,
    includeBookingLink: true
  };

  const nurtureCadence = settings?.nurtureCadence || DEFAULT_CADENCE;

  const handleAIChange = (field, value) => {
    onChange({
      ...settings,
      aiSettings: { ...aiSettings, [field]: value }
    });
  };

  const handleCadenceChange = (temp, delays) => {
    onChange({
      ...settings,
      nurtureCadence: { ...nurtureCadence, [temp]: delays }
    });
  };

  const addTopic = (type) => {
    const key = type === 'emphasize' ? 'emphasizeTopics' : 'avoidTopics';
    const value = newTopic[type].trim();
    if (!value) return;

    const current = aiSettings[key] || [];
    if (!current.includes(value)) {
      handleAIChange(key, [...current, value]);
    }
    setNewTopic({ ...newTopic, [type]: '' });
  };

  const removeTopic = (type, topic) => {
    const key = type === 'emphasize' ? 'emphasizeTopics' : 'avoidTopics';
    handleAIChange(key, (aiSettings[key] || []).filter(t => t !== topic));
  };

  const formatDelay = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${hours} hrs`;
    return `${Math.round(hours / 24)} days`;
  };

  const currentTone = TONES.find(t => t.id === aiSettings.tone) || TONES[0];
  const currentSignOff = SIGN_OFFS.find(s => s.id === (aiSettings.signOff || 'warmly')) || SIGN_OFFS[0];

  const getSignatureBlock = () => {
    if (!hasValidSignature) {
      return '\n\n[Configure your signature in the Settings tab]';
    }
    const signOffText = currentSignOff.preview;
    const name = signature.name;
    const title = signature.title ? `\n${signature.title}` : '';
    const business = signature.businessName && signature.businessName !== signature.name
      ? `\n${signature.businessName}`
      : '';
    return `\n\n${signOffText}\n${name}${title}${business}`;
  };

  const getDisplaySubject = () => {
    if (aiSettings.tone === 'custom') {
      return aiSettings.customSubject || 'Enter your custom subject line...';
    }
    return currentTone.subject;
  };

  const getDisplayBody = () => {
    if (aiSettings.tone === 'custom') {
      return (aiSettings.customBody || 'Write your custom email template here...\n\nUse {{name}}, {{project_type}}, and {{event_date}} as placeholders.') + getSignatureBlock();
    }
    return currentTone.bodyTemplate + getSignatureBlock();
  };

  const startEditing = () => {
    setEditedSubject(aiSettings.tone === 'custom' ? (aiSettings.customSubject || '') : currentTone.subject);
    setEditedBody(aiSettings.tone === 'custom' ? (aiSettings.customBody || '') : currentTone.bodyTemplate);
    setIsEditingEmail(true);
  };

  const saveEdits = () => {
    if (aiSettings.tone !== 'custom') {
      handleAIChange('tone', 'custom');
    }
    onChange({
      ...settings,
      aiSettings: {
        ...aiSettings,
        tone: 'custom',
        customSubject: editedSubject,
        customBody: editedBody
      }
    });
    setIsEditingEmail(false);
  };

  const cancelEdits = () => {
    setIsEditingEmail(false);
  };

  const Toggle = ({ checked, onChange: onToggle, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-[14px] font-medium text-black dark:text-white">{label}</p>
        {description && (
          <p className="text-[12px] text-black/40 dark:text-white/40">{description}</p>
        )}
      </div>
      <button
        onClick={() => onToggle(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          checked ? 'bg-brand' : 'bg-black/10 dark:bg-white/10'
        }`}
      >
        <motion.div
          animate={{ x: checked ? 20 : 2 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Main Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-black/60 dark:text-white/60" />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white">AI-Powered Follow-ups</h3>
            <p className="text-[12px] text-black/50 dark:text-white/50">
              Personalized emails sent automatically
            </p>
          </div>
        </div>
        <button
          onClick={() => handleAIChange('enabled', !aiSettings.enabled)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            aiSettings.enabled ? 'bg-brand' : 'bg-black/10 dark:bg-white/10'
          }`}
        >
          <motion.div
            animate={{ x: aiSettings.enabled ? 20 : 2 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
          />
        </button>
      </div>

      <AnimatePresence>
        {aiSettings.enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-8 overflow-hidden"
          >
            {/* Tone Selection */}
            <div>
              <h3 className="text-[13px] font-medium text-black/50 dark:text-white/50 uppercase tracking-wide mb-3">
                Voice & Tone
              </h3>
              <div className="flex gap-2">
                {TONES.map((tone) => (
                  <button
                    key={tone.id}
                    onClick={() => handleAIChange('tone', tone.id)}
                    className={`flex-1 px-4 py-3 rounded-xl border transition-all ${
                      aiSettings.tone === tone.id
                        ? 'border-brand bg-brand/5'
                        : 'border-black/5 dark:border-white/10 hover:border-black/10 dark:hover:border-white/20'
                    }`}
                  >
                    <p className={`text-[14px] font-medium ${
                      aiSettings.tone === tone.id
                        ? 'text-brand'
                        : 'text-black dark:text-white'
                    }`}>
                      {tone.name}
                    </p>
                    <p className="text-[11px] text-black/40 dark:text-white/40 mt-0.5">
                      {tone.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Signature Warning */}
            {!hasValidSignature && (
              <div className="p-4 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10">
                <p className="text-[14px] font-medium text-black dark:text-white mb-1">
                  Signature Required
                </p>
                <p className="text-[13px] text-black/50 dark:text-white/50">
                  Set up your email signature in the Settings tab before emails can be sent.
                </p>
              </div>
            )}

            {/* Sign-off Style */}
            <div>
              <h3 className="text-[13px] font-medium text-black/50 dark:text-white/50 uppercase tracking-wide mb-3">
                Email Sign-off
              </h3>
              <div className="flex flex-wrap gap-2">
                {SIGN_OFFS.map((signOff) => (
                  <button
                    key={signOff.id}
                    onClick={() => handleAIChange('signOff', signOff.id)}
                    className={`px-4 py-2 rounded-lg border transition-all text-[13px] font-medium ${
                      (aiSettings.signOff || 'warmly') === signOff.id
                        ? 'border-brand bg-brand/5 text-brand'
                        : 'border-black/5 dark:border-white/10 text-black/60 dark:text-white/60 hover:border-black/20 dark:hover:border-white/20'
                    }`}
                  >
                    {signOff.label}
                  </button>
                ))}
              </div>
              {hasValidSignature && (
                <p className="text-[12px] text-black/40 dark:text-white/40 mt-2">
                  Emails will be signed: {currentSignOff.preview} {signature.name}
                </p>
              )}
            </div>

            {/* Email Preview / Editor */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[13px] font-medium text-black/50 dark:text-white/50 uppercase tracking-wide">
                  Email Preview
                </h3>
                {!isEditingEmail ? (
                  <button
                    onClick={startEditing}
                    className="flex items-center gap-1.5 text-[13px] font-medium text-black/50 dark:text-white/50
                      hover:text-brand transition-colors"
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                    Edit
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={cancelEdits}
                      className="text-[13px] font-medium text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEdits}
                      className="px-3 py-1.5 rounded-lg bg-brand text-white text-[13px] font-medium"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>

              {/* Email Preview Card */}
              <div className="rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden">
                {/* Email Header */}
                <div className="px-5 py-4 bg-black/[0.02] dark:bg-white/[0.02] border-b border-black/5 dark:border-white/5">
                  {isEditingEmail ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-[11px] font-medium text-black/40 dark:text-white/40 uppercase">
                          Subject
                        </label>
                        <input
                          type="text"
                          value={editedSubject}
                          onChange={(e) => setEditedSubject(e.target.value)}
                          placeholder="Email subject line..."
                          className="w-full mt-1 px-0 py-1 bg-transparent border-none text-[15px] font-medium
                            text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30
                            focus:outline-none focus:ring-0"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[11px] font-medium text-black/40 dark:text-white/40 uppercase mb-1">
                        Subject
                      </p>
                      <p className="text-[15px] font-medium text-black dark:text-white">
                        {getDisplaySubject()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Email Body */}
                <div className="px-5 py-4">
                  {isEditingEmail ? (
                    <div>
                      <label className="text-[11px] font-medium text-black/40 dark:text-white/40 uppercase">
                        Body
                      </label>
                      <textarea
                        value={editedBody}
                        onChange={(e) => setEditedBody(e.target.value)}
                        placeholder="Write your email template..."
                        rows={12}
                        className="w-full mt-1 px-0 py-1 bg-transparent border-none text-[14px] leading-relaxed
                          text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30
                          focus:outline-none focus:ring-0 resize-none"
                      />
                    </div>
                  ) : (
                    <div className="text-[14px] leading-relaxed text-black/70 dark:text-white/70 whitespace-pre-wrap">
                      {getDisplayBody()}
                    </div>
                  )}
                </div>

                {/* Placeholders Legend */}
                <div className="px-5 py-3 bg-black/[0.02] dark:bg-white/[0.02] border-t border-black/5 dark:border-white/5">
                  <p className="text-[11px] text-black/40 dark:text-white/40">
                    <span className="font-medium">Placeholders:</span>{' '}
                    <span className="font-mono">{'{{name}}'}</span> ·
                    <span className="font-mono"> {'{{project_type}}'}</span> ·
                    <span className="font-mono"> {'{{event_date}}'}</span>
                    <span className="ml-3 text-black/30 dark:text-white/30">Signature added automatically</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Topics */}
            <div className="space-y-4">
              <h3 className="text-[13px] font-medium text-black/50 dark:text-white/50 uppercase tracking-wide">
                Content Guidelines
              </h3>

              {/* Emphasize Topics */}
              <div className="p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                <label className="block text-[13px] font-medium text-black dark:text-white mb-2">
                  Emphasize in emails
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(aiSettings.emphasizeTopics || []).map((topic) => (
                    <span
                      key={topic}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                        bg-black/5 dark:bg-white/10 text-black/70 dark:text-white/70 text-[13px]"
                    >
                      {topic}
                      <button
                        onClick={() => removeTopic('emphasize', topic)}
                        className="hover:text-black dark:hover:text-white"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {(aiSettings.emphasizeTopics || []).length === 0 && (
                    <span className="text-[13px] text-black/30 dark:text-white/30">
                      No topics added
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTopic.emphasize}
                    onChange={(e) => setNewTopic({ ...newTopic, emphasize: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && addTopic('emphasize')}
                    placeholder="e.g., storytelling, emotion, quality"
                    className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-white/5
                      border border-black/10 dark:border-white/10 text-black dark:text-white text-[13px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                  />
                  <button
                    onClick={() => addTopic('emphasize')}
                    className="px-3 py-2 rounded-lg bg-black/5 dark:bg-white/10
                      text-black/60 dark:text-white/60 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Avoid Topics */}
              <div className="p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                <label className="block text-[13px] font-medium text-black dark:text-white mb-2">
                  Never mention
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(aiSettings.avoidTopics || []).map((topic) => (
                    <span
                      key={topic}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                        bg-black/5 dark:bg-white/10 text-black/70 dark:text-white/70 text-[13px]"
                    >
                      {topic}
                      <button
                        onClick={() => removeTopic('avoid', topic)}
                        className="hover:text-black dark:hover:text-white"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {(aiSettings.avoidTopics || []).length === 0 && (
                    <span className="text-[13px] text-black/30 dark:text-white/30">
                      No topics added
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTopic.avoid}
                    onChange={(e) => setNewTopic({ ...newTopic, avoid: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && addTopic('avoid')}
                    placeholder="e.g., pricing, discounts, competitors"
                    className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-white/5
                      border border-black/10 dark:border-white/10 text-black dark:text-white text-[13px]
                      placeholder:text-black/30 dark:placeholder:text-white/30
                      focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                  />
                  <button
                    onClick={() => addTopic('avoid')}
                    className="px-3 py-2 rounded-lg bg-black/5 dark:bg-white/10
                      text-black/60 dark:text-white/60 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Follow-up Cadence */}
            <div>
              <button
                onClick={() => setShowCadence(!showCadence)}
                className="flex items-center gap-2 w-full"
              >
                <h3 className="text-[13px] font-medium text-black/50 dark:text-white/50 uppercase tracking-wide">
                  Follow-up Schedule
                </h3>
                <ChevronDownIcon className={`w-4 h-4 text-black/30 dark:text-white/30 transition-transform ${showCadence ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showCadence && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="space-y-3 overflow-hidden mt-4"
                  >
                    {/* Cold Leads */}
                    <div className="p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-black/20 dark:bg-white/20" />
                          <span className="text-[14px] font-medium text-black dark:text-white">Cold Leads</span>
                        </div>
                        <span className="text-[12px] text-black/40 dark:text-white/40">Score &lt; 40</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(nurtureCadence.cold || DEFAULT_CADENCE.cold).map((delay, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="px-2.5 py-1 rounded-md bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60 text-[12px] font-medium">
                              {formatDelay(delay)}
                            </span>
                            {i < (nurtureCadence.cold || DEFAULT_CADENCE.cold).length - 1 && (
                              <span className="text-black/20 dark:text-white/20">→</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Warm Leads */}
                    <div className="p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-brand" />
                          <span className="text-[14px] font-medium text-black dark:text-white">Warm Leads</span>
                        </div>
                        <span className="text-[12px] text-black/40 dark:text-white/40">Score 40-69</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(nurtureCadence.warm || DEFAULT_CADENCE.warm).map((delay, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="px-2.5 py-1 rounded-md bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60 text-[12px] font-medium">
                              {formatDelay(delay)}
                            </span>
                            {i < (nurtureCadence.warm || DEFAULT_CADENCE.warm).length - 1 && (
                              <span className="text-black/20 dark:text-white/20">→</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hot Leads */}
                    <div className="p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-black dark:bg-white" />
                          <span className="text-[14px] font-medium text-black dark:text-white">Hot Leads</span>
                        </div>
                        <span className="text-[12px] text-black/40 dark:text-white/40">Score 70+</span>
                      </div>
                      <p className="text-[13px] text-black/50 dark:text-white/50">
                        No auto-follow up. You'll be notified immediately to handle personally.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Additional Settings */}
            <div className="space-y-1 border-t border-black/5 dark:border-white/5 pt-6">
              <h3 className="text-[13px] font-medium text-black/50 dark:text-white/50 uppercase tracking-wide mb-3">
                Behavior
              </h3>

              <Toggle
                checked={aiSettings.stopOnReply}
                onChange={(v) => handleAIChange('stopOnReply', v)}
                label="Stop on reply"
                description="Pause sequence when lead responds"
              />

              <Toggle
                checked={aiSettings.includeBookingLink}
                onChange={(v) => handleAIChange('includeBookingLink', v)}
                label="Include booking link"
                description="Add link to schedule a call"
              />

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-[14px] font-medium text-black dark:text-white">Max emails per lead</p>
                  <p className="text-[12px] text-black/40 dark:text-white/40">
                    Cap on nurture sequence length
                  </p>
                </div>
                <select
                  value={aiSettings.maxEmailsPerLead || 5}
                  onChange={(e) => handleAIChange('maxEmailsPerLead', parseInt(e.target.value))}
                  className="px-3 py-2 rounded-lg bg-black/[0.03] dark:bg-white/5
                    border border-black/5 dark:border-white/10 text-black dark:text-white text-[14px]
                    focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
                >
                  {[3, 4, 5, 6, 7].map(n => (
                    <option key={n} value={n}>{n} emails</option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AISettingsPanel;
